import React, { useMemo, useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { ORDER_STATUS } from '../../firebase/orders';
import logo from '../../assets/images/logos/JG Printing.png';
import AdminNotificationsModal from '../AdminNotificationsModal/AdminNotificationsModal';
import './AdminLayout.css';
import { getDefaultAdminRoute, getRoleLabel, isAdminPanelRole, normalizeUserRole, USER_ROLES } from '../../utils/adminRoles';

const navItems = [
    { to: '/admin/dashboard', base: '/admin/dashboard', label: 'Dashboard', icon: 'dashboard', roles: [USER_ROLES.MASTER_ADMIN, USER_ROLES.ADMIN] },
    {
        to: '/admin/products/manage',
        base: '/admin/products',
        label: 'Products',
        icon: 'products',
        roles: [USER_ROLES.MASTER_ADMIN, USER_ROLES.ADMIN],
        children: [
            { label: 'Manage Products', to: '/admin/products/manage' },
            { label: 'Manage Category', to: '/admin/products/categories' },
            { label: 'Manage Subcategory', to: '/admin/products/subcategories' },
            { label: 'Manage Submenus', to: '/admin/products/submenus' },
            { label: 'Manage Attributes', to: '/admin/products/attributes' },
            { label: 'Manage Inventory', to: '/admin/products/inventory' }
        ]
    },
    {
        to: '/admin/orders/list',
        base: '/admin/orders',
        label: 'Orders',
        icon: 'orders',
        roles: [USER_ROLES.MASTER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.STAFF],
        children: [
            { label: 'Order List', to: '/admin/orders/list' },
            { label: 'Order Details', to: '/admin/orders/details' }
        ]
    },
    {
        to: '/admin/promotions/manage',
        base: '/admin/promotions',
        label: 'Promotions',
        icon: 'promotions',
        roles: [USER_ROLES.MASTER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.STAFF],
        children: [
            { label: 'Manage Promotions', to: '/admin/promotions/manage' },
            { label: 'Promotion Details', to: '/admin/promotions/details' }
        ]
    },
    { to: '/admin/collections', base: '/admin/collections', label: 'Collections', icon: 'collections', roles: [USER_ROLES.MASTER_ADMIN, USER_ROLES.ADMIN] },
    { to: '/admin/chats',       base: '/admin/chats',       label: 'Chats',       icon: 'chats', roles: [USER_ROLES.MASTER_ADMIN, USER_ROLES.ADMIN] },
    {
        to: '/admin/reports/sales',
        base: '/admin/reports',
        label: 'Reports',
        icon: 'reports',
        roles: [USER_ROLES.MASTER_ADMIN, USER_ROLES.ADMIN],
        children: [
            { label: 'Sales & Order Report', to: '/admin/reports/sales' },
            { label: 'Inventory Report', to: '/admin/reports/inventory' },
            { label: 'Stats', to: '/admin/reports/customers' },
        ]
    },
    {
        to: '/admin/settings/account',
        base: '/admin/settings',
        label: 'Settings',
        icon: 'settings',
        roles: [USER_ROLES.MASTER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.STAFF],
        children: [
            { label: 'Store Settings',         to: '/admin/settings/store', roles: [USER_ROLES.MASTER_ADMIN, USER_ROLES.ADMIN] },
            { label: 'Homepage Settings',      to: '/admin/settings/homepage', roles: [USER_ROLES.MASTER_ADMIN, USER_ROLES.ADMIN] },
            { label: 'Admin Account Settings', to: '/admin/settings/account', roles: [USER_ROLES.MASTER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.STAFF] },
        ]
    }
];

const iconMap = {
    dashboard: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-6v-6H10v6H4a1 1 0 0 1-1-1v-9z"/>
        </svg>
    ),
    products: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 7l9-4 9 4-9 4-9-4z"/>
            <path d="M3 7v10l9 4 9-4V7"/>
            <path d="M12 11v10"/>
        </svg>
    ),
    orders: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 2h8l3 3v17a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z"/>
            <path d="M16 2v4h4"/>
            <path d="M10 10h6M10 14h6M10 18h4"/>
        </svg>
    ),
    promotions: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 3h10l4 4v10l-4 4H7l-4-4V7l4-4z"/>
            <path d="M8.5 8.5a1.5 1.5 0 1 0 3 0 1.5 1.5 0 0 0-3 0zm7 7a1.5 1.5 0 1 0 3 0 1.5 1.5 0 0 0-3 0z"/>
            <path d="M15 9l-6 6"/>
        </svg>
    ),
    collections: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="8" height="8" rx="1"/>
            <rect x="13" y="3" width="8" height="8" rx="1"/>
            <rect x="3" y="13" width="8" height="8" rx="1"/>
            <rect x="13" y="13" width="8" height="8" rx="1"/>
        </svg>
    ),
    chats: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9l-5 4v-4H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/>
            <path d="M7 9h10M7 13h6"/>
        </svg>
    ),
    reports: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/>
            <path d="M8 17V9M12 17V7M16 17v-4"/>
        </svg>
    ),
    settings: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.5 1.5 0 0 0 .3 1.7l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.5 1.5 0 0 0-1.7-.3 1.5 1.5 0 0 0-.9 1.4V22a2 2 0 1 1-4 0v-.1a1.5 1.5 0 0 0-.9-1.4 1.5 1.5 0 0 0-1.7.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.5 1.5 0 0 0 .3-1.7 1.5 1.5 0 0 0-1.4-.9H2a2 2 0 1 1 0-4h.1a1.5 1.5 0 0 0 1.4-.9 1.5 1.5 0 0 0-.3-1.7l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.5 1.5 0 0 0 1.7.3 1.5 1.5 0 0 0 .9-1.4V2a2 2 0 1 1 4 0v.1a1.5 1.5 0 0 0 .9 1.4 1.5 1.5 0 0 0 1.7-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.5 1.5 0 0 0-.3 1.7 1.5 1.5 0 0 0 1.4.9H22a2 2 0 1 1 0 4h-.1a1.5 1.5 0 0 0-1.4.9z"/>
        </svg>
    ),
};

// ── Firestore meta docs (same as AdminNotificationsModal) ─────────────────────
const READ_IDS_DOC  = doc(db, 'settings', 'adminNotifReadIds');
const DISMISSED_DOC = doc(db, 'settings', 'adminNotifDismissed');

function AdminLayout() {
    const [isAccountOpen, setIsAccountOpen] = useState(false);
    const [isNotifOpen,   setIsNotifOpen]   = useState(false);
    const [notifCount,    setNotifCount]    = useState(0);
    const [accountLabel,  setAccountLabel]  = useState('Account');
    const [sidebarOpen,   setSidebarOpen]   = useState(false);
    const [expandedNav,   setExpandedNav]   = useState(() => ({
        '/admin/products':   false,
        '/admin/orders':     false,
        '/admin/promotions': false,
        '/admin/reports':    false,
        '/admin/settings':   false,
    }));

    const { currentUser, logOut, userRole } = useAuth();
    const accountRef = React.useRef(null);
    const navigate       = useNavigate();
    const location       = useLocation();
    const normalizedRole = normalizeUserRole(userRole);
    const visibleNavItems = useMemo(
        () => navItems
            .filter((item) => !item.roles || item.roles.includes(normalizedRole))
            .map((item) => ({
                ...item,
                children: item.children?.filter((child) => !child.roles || child.roles.includes(normalizedRole)),
            })),
        [normalizedRole]
    );

    // ── Badge count ───────────────────────────────────────────────────────────
    // Re-fetches dismissedIds + readIds from Firestore on every snapshot update
    // so the badge is always accurate after Remove All or Mark All Read.

    useEffect(() => {
        function handleClickOutside(e) {
            if (accountRef.current && !accountRef.current.contains(e.target)) {
                setIsAccountOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        let unsubOrders = () => {};
        let unsubChats  = () => {};
        let latestOrderIds = new Set();
        let latestChatIds  = new Set();

        // Fetch meta from Firestore and compute unread count
        const recalc = async (orderIds, chatIds) => {
            let dismissedIds = new Set();
            let readIds      = new Set();
            try {
                const [dismissedSnap, readSnap] = await Promise.all([
                    getDoc(DISMISSED_DOC),
                    getDoc(READ_IDS_DOC),
                ]);
                if (dismissedSnap.exists()) dismissedIds = new Set(dismissedSnap.data().ids || []);
                if (readSnap.exists())      readIds      = new Set(readSnap.data().ids      || []);
            } catch { /* use empty sets */ }

            const allIds  = new Set([...orderIds, ...chatIds]);
            const visible = [...allIds].filter(id => chatIds.has(id) || !dismissedIds.has(id));
            const unread  = visible.filter(id => chatIds.has(id) || !readIds.has(id)).length;
            setNotifCount(unread);
        };

        const ordersQuery = query(
            collection(db, 'orders'),
            where('status', 'in', [
                ORDER_STATUS.PENDING_APPROVAL,
                'pending',
                ORDER_STATUS.AWAITING_PAYMENT,
            ])
        );

        unsubOrders = onSnapshot(ordersQuery, (snap) => {
            // Build IDs using the same format as buildNotifications() in AdminNotificationsModal
            latestOrderIds = new Set(
                snap.docs.flatMap(d => {
                    const data   = d.data();
                    const status = data.status;
                    const ids    = [];
                    if (status === ORDER_STATUS.PENDING_APPROVAL || status === 'pending') {
                        ids.push(`order-pending-${d.id}`);
                    }
                    if (status === ORDER_STATUS.AWAITING_PAYMENT) {
                        ids.push(`order-gcash-${d.id}`);
                    }
                    return ids;
                })
            );
            recalc(latestOrderIds, latestChatIds);
        }, (err) => console.warn('Admin notif orders badge error:', err.message));

        const chatsQuery = query(
            collection(db, 'chats'),
            where('unreadByAdmin', '>', 0)
        );

        unsubChats = onSnapshot(chatsQuery, (snap) => {
            latestChatIds = new Set(snap.docs.map(d => `chat-${d.id}`));
            recalc(latestOrderIds, latestChatIds);
        }, (err) => console.warn('Admin notif chats badge error:', err.message));

        return () => { unsubOrders(); unsubChats(); };
    }, []);

    useEffect(() => {
        if (!isAdminPanelRole(normalizedRole)) return;
        if (visibleNavItems.some((item) => location.pathname.startsWith(item.base))) return;
        navigate(getDefaultAdminRoute(normalizedRole), { replace: true });
    }, [location.pathname, navigate, normalizedRole, visibleNavItems]);

    useEffect(() => {
        let isMounted = true;

        async function loadAccountLabel() {
            if (!currentUser?.uid) {
                if (isMounted) setAccountLabel('Account');
                return;
            }
            try {
                const [adminSnap, userSnap] = await Promise.all([
                    getDoc(doc(db, 'admins', currentUser.uid)),
                    getDoc(doc(db, 'users', currentUser.uid)),
                ]);
                const adminData = adminSnap.exists() ? adminSnap.data() : {};
                const userData  = userSnap.exists()  ? userSnap.data()  : {};
                const fallback  = currentUser.email?.split('@')[0] || 'Account';
                const resolved  =
                    adminData?.adminName?.trim()
                    || [userData?.firstName, userData?.lastName].filter(Boolean).join(' ').trim()
                    || currentUser.displayName?.trim()
                    || fallback;
                if (isMounted) setAccountLabel(resolved);
            } catch {
                const fallback = currentUser.email?.split('@')[0] || 'Account';
                if (isMounted) setAccountLabel(currentUser.displayName?.trim() || fallback);
            }
        }

        loadAccountLabel();
        return () => { isMounted = false; };
    }, [currentUser]);

    // When the modal closes, re-fetch Firestore meta and recompute badge
    const handleNotifClose = () => {
        setIsNotifOpen(false);
        // Don't reset to 0 — let the live Firestore listeners keep the count accurate
        };

    const handleToggleAccount = () => {
        setIsAccountOpen(prev => !prev);
        setIsNotifOpen(false);
    };

    const handleToggleNotif = () => {
        setIsNotifOpen(prev => !prev);
        setIsAccountOpen(false);
    };

    const handleLogout = async () => {
        const result = await logOut();
        if (result?.error) { console.error('Logout error:', result.error); return; }
        navigate('/', { replace: true });
    };

    const handleViewAsUser = () => {
        window.open('/?preview=admin', '_blank');
    };

    const pageTitle = useMemo(() => {
        for (const item of visibleNavItems) {
            if (!location.pathname.startsWith(item.base)) continue;
            if (item.children) {
                const child = item.children.find(c => location.pathname.startsWith(c.to));
                if (child) return child.label;
            }
            return item.label;
        }
        return 'Dashboard';
    }, [location.pathname, visibleNavItems]);

    const handleToggleNav    = (target) => setExpandedNav(prev => ({ ...prev, [target]: !prev[target] }));
    const handleCloseSidebar = () => setSidebarOpen(false);

    return (
        <div className="admin-layout">
            <div
                className={`admin-sidebar-overlay ${sidebarOpen ? 'is-open' : ''}`}
                onClick={handleCloseSidebar}
            />

            <aside className={`admin-sidebar ${sidebarOpen ? 'is-open' : ''}`}>
                <button
                    type="button"
                    className="admin-sidebar-close"
                    onClick={handleCloseSidebar}
                    aria-label="Close menu"
                >
                    <span>&times;</span>
                </button>

                <div className="admin-brand admin-sidebar-brand">
                    <div className="admin-logo">
                        <img src={logo} alt="JG Printing" />
                    </div>
                    <div className="admin-brand-text">
                        <span className="admin-brand-name">JG Printing</span>
                        <span className="admin-brand-role">Since 2019</span>
                    </div>
                </div>

                <nav className="admin-nav">
                    {visibleNavItems.map((item) => {
                        const isExpanded    = expandedNav[item.base];
                        const hasChildren   = Boolean(item.children?.length);
                        const isActiveRoute = location.pathname.startsWith(item.base);

                        return (
                            <div key={item.to} className="admin-nav-group">
                                <NavLink
                                    to={item.to}
                                    className={() => `admin-nav-item${isActiveRoute ? ' is-active' : ''}`}
                                    onClick={() => { if (!hasChildren) handleCloseSidebar(); }}
                                >
                                    <span className="admin-nav-icon">{iconMap[item.icon]}</span>
                                    <span className="admin-nav-label">{item.label}</span>
                                    {hasChildren && (
                                        <button
                                            type="button"
                                            className={`admin-nav-caret${isExpanded ? ' is-open' : ''}`}
                                            onClick={(e) => { e.preventDefault(); handleToggleNav(item.base); }}
                                            aria-label={`Toggle ${item.label}`}
                                        >
                                            <span />
                                        </button>
                                    )}
                                </NavLink>

                                {hasChildren && (
                                    <div className={`admin-subnav${isExpanded ? ' is-open' : ''}`}>
                                        {item.children.map(child => (
                                            <NavLink
                                                key={child.to}
                                                to={child.to}
                                                className={({ isActive }) => `admin-subnav-item${isActive ? ' is-active' : ''}`}
                                                onClick={handleCloseSidebar}
                                            >
                                                {child.label}
                                            </NavLink>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </nav>
            </aside>

            <div className="admin-body">
                <header className="admin-topbar">
                    <div className="admin-topbar-left">
                        <button
                            type="button"
                            className="admin-mobile-menu-btn"
                            onClick={() => setSidebarOpen(true)}
                            aria-label="Open menu"
                        >
                            <span /><span /><span />
                        </button>
                        <h1 className="admin-page-title">{pageTitle}</h1>
                    </div>

                    <div className="admin-top-actions">
                        <button
                            type="button"
                            className="admin-view-as-user-btn"
                            onClick={handleViewAsUser}
                            title="Open customer site in preview mode"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                <circle cx="12" cy="12" r="3"/>
                            </svg>
                            View as User
                        </button>

                        <div className="admin-notif-wrapper">
                            <button
                                type="button"
                                className={`admin-top-action${isNotifOpen ? ' is-active' : ''}`}
                                onClick={handleToggleNotif}
                                aria-label="Notifications"
                            >
                                <span className="admin-top-icon">
                                    <svg viewBox="0 0 24 24">
                                        <path d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22zm7-6V11a7 7 0 1 0-14 0v5l-2 2v1h18v-1l-2-2z"/>
                                    </svg>
                                </span>
                                Notifications
                                {notifCount > 0 && (
                                    <span className="admin-notif-badge">{notifCount}</span>
                                )}
                            </button>
                            <AdminNotificationsModal
                                isOpen={isNotifOpen}
                                onClose={handleNotifClose}
                            />
                        </div>

                        <div className="admin-account" ref={accountRef}>

                            <button
                                type="button"
                                className="admin-top-action"
                                onClick={handleToggleAccount}
                                aria-expanded={isAccountOpen}
                                aria-haspopup="menu"
                            >
                                <span className="admin-top-icon">
                                    <svg viewBox="0 0 24 24">
                                        <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4zm0 2c-4 0-7 2-7 4v2h14v-2c0-2-3-4-7-4z"/>
                                    </svg>
                                </span>
                                {accountLabel}
                                <span className="admin-account-role">{getRoleLabel(normalizedRole)}</span>
                            </button>
                            {isAccountOpen && (
                                <div className="admin-account-menu" role="menu">
                                    <button
                                        type="button"
                                        className="admin-account-item"
                                        onClick={handleLogout}
                                        role="menuitem"
                                    >
                                        Logout
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <main className="admin-main">
                    <div className="admin-content">
                        <Outlet />
                    </div>
                </main>
            </div>
        </div>
    );
}

export default AdminLayout;
